import { useTheme } from '@gousto-internal/citrus-react'
import { canUseWindow } from '@library/environment'
import { useEffect, useState } from 'react'

const getIsDesktop = (desktopBreakpoint: number) =>
  canUseWindow() ? window.innerWidth >= desktopBreakpoint : true

/**
 * True once the viewport reaches Citrus's `Large` breakpoint (1024px) — for layouts that switch
 * to side-by-side there. `@library/dom`'s `useDeviceType` isn't a fit: its "desktop" bucket starts
 * at 768px, one tier earlier.
 *
 * SSR-safe: the initial value is a constant `false`, not the real viewport, so the server render
 * and the client's first render agree (no hydration mismatch). Reading `window.innerWidth` in the
 * `useState` initializer would return the actual width on the client's first render but `true` on
 * the server, and any markup that branches on this (e.g. the CwC supplier carousel's nav buttons)
 * would then differ between server and client. The real breakpoint is resolved in the effect below,
 * once mounted — so desktop viewports upgrade to `true` immediately after hydration.
 */
export const useIsDesktopBreakpoint = (): boolean => {
  const { theme } = useTheme()
  const desktopBreakpoint = parseInt(theme.tokens.breakpoints[2], 10)

  const [isDesktop, setIsDesktop] = useState(false)

  useEffect(() => {
    if (!canUseWindow()) return

    const onResize = () => setIsDesktop(getIsDesktop(desktopBreakpoint))
    onResize() // resolve the actual breakpoint once mounted (after hydration)
    window.addEventListener('resize', onResize)

    return () => window.removeEventListener('resize', onResize)
  }, [desktopBreakpoint])

  return isDesktop
}
